import React from 'react';
import { Store, Stethoscope, CheckCircle2, ArrowRight, Phone, Mail } from 'lucide-react';

const JoinUs = () => {
  const shopBenefits = [
    'Free listing — no subscription, no commission ever',
    'Your shop appears in filtered search results for your pet types & services',
    'Reach pet owners actively looking for boarding in your area',
    'Our team writes and manages your listing for you',
    'Update your availability or hours by calling us — no tech skills needed',
    'Build a verified reputation with real owner reviews',
  ];

  const vetBenefits = [
    'Free listing — verified by State Veterinary Council registration check',
    'Appear in emergency searches filtered by your specialty (surgery, critical care, exotics)',
    'Receive calls from pet owners who already know their situation — faster triage',
    'Your clinic address, hours, and distance shown to owners in crisis',
    'No commission on consultations — you keep 100% of your earnings',
    'Builds your professional credibility with verified badge on your profile',
  ];

  const process = [
    { step: '01', title: 'Fill a Simple Form', desc: 'Tell us your business name, location, services, and available contact. Takes under 5 minutes.' },
    { step: '02', title: 'We Visit & Verify', desc: 'Our team contacts you to understand your facility or practice. For vets, we verify council registration. For shops, we confirm your boarding setup.' },
    { step: '03', title: 'We Write Your Listing', desc: 'We handle the content — your services, specialties, highlights. You review and approve. No tech knowledge required.' },
    { step: '04', title: 'Go Live. Get Calls.', desc: 'Your listing goes live and pet owners across Coimbatore start finding you — exactly when they need exactly what you offer.' },
  ];

  return (
    <section id="join" className="py-24 bg-gray-50 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-brand-orange font-bold tracking-widest uppercase text-xs mb-3 bg-orange-50 px-4 py-1.5 rounded-full border border-orange-100">
            For Businesses
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-5">
            Are You a Pet Shop Owner<br/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-orange to-brand-teal">
              or a Veterinarian?
            </span>
          </h2>
          <p className="text-xl text-gray-500 leading-relaxed">
            PetCares is free for the businesses that make pet care possible.
            We bring the pet owners to you — you focus on doing what you do best.
          </p>
        </div>

        {/* Partner Image */}
        <div className="max-w-5xl mx-auto mb-20 rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white h-72 lg:h-[450px] relative group">
          <img src={require('../assets/images/partners.png')} alt="PetCares Partners" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 hover:object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60" />
          <div className="absolute bottom-6 left-6 text-white text-left">
            <h3 className="font-extrabold text-2xl mb-1">Growth Without Fees</h3>
            <p className="text-sm text-gray-200">A community where premium pet care meets verified clients.</p>
          </div>
        </div>

        {/* Two audience cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {/* Pet Shop */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-brand-orange to-amber-400 p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Store className="text-white" size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-white">Pet Shop & Boarding Owners</h3>
                <p className="text-orange-100 text-sm">Reach pet owners actively looking for boarding in your area</p>
              </div>
            </div>
            <div className="p-7">
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Whether you run a full pet boarding centre, a grooming studio, a daycare service, or a pet supply store that also offers temporary boarding — PetCares helps pet owners in Coimbatore discover and contact you at exactly the moment they need help.
              </p>
              <ul className="space-y-3 mb-8">
                {shopBenefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <CheckCircle2 size={16} className="text-brand-orange flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href="tel:+919842111290"
                className="flex items-center justify-center gap-2 w-full py-4 bg-brand-orange text-white font-bold rounded-2xl hover:bg-amber-600 transition-all shadow-md shadow-orange-200"
              >
                <Phone size={18} /> Call to Register Your Shop
              </a>
              <a
                href="mailto:shops@petcares.in"
                className="flex items-center justify-center gap-2 w-full py-3 mt-3 text-brand-orange font-semibold rounded-2xl border border-orange-100 hover:bg-orange-50 transition text-sm"
              >
                <Mail size={16} /> shops@petcares.in
              </a>
            </div>
          </div>

          {/* Vets */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-emergency-red to-rose-500 p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Stethoscope className="text-white" size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-white">Licensed Veterinarians</h3>
                <p className="text-red-100 text-sm">Connect with genuine emergency cases in your area</p>
              </div>
            </div>
            <div className="p-7">
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Pet emergencies in Coimbatore happen every night. Most owners spend 30 precious minutes searching Google for an available vet. PetCares puts your number directly in front of those owners — filtered by your specialty, your location, and your availability. You receive better-quality calls. Pets get care faster.
              </p>
              <ul className="space-y-3 mb-8">
                {vetBenefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <CheckCircle2 size={16} className="text-emergency-red flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href="tel:+919842111290"
                className="flex items-center justify-center gap-2 w-full py-4 bg-emergency-red text-white font-bold rounded-2xl hover:bg-red-700 transition-all shadow-md shadow-red-200"
              >
                <Phone size={18} /> Call to Register as a Vet
              </a>
              <a
                href="mailto:vets@petcares.in"
                className="flex items-center justify-center gap-2 w-full py-3 mt-3 text-emergency-red font-semibold rounded-2xl border border-red-100 hover:bg-red-50 transition text-sm"
              >
                <Mail size={16} /> vets@petcares.in
              </a>
            </div>
          </div>
        </div>

        {/* Registration process */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-12">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-extrabold text-gray-900 mb-3">How We Onboard Partners</h3>
            <p className="text-gray-500">Getting listed takes under a day. Here is our simple 4-step process — we do the heavy lifting.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <div key={i} className="text-center relative">
                <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-teal font-black text-lg">{p.step}</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{p.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                {i < 3 && (
                  <ArrowRight className="hidden lg:block absolute -right-3 top-4 text-gray-300" size={20} />
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default JoinUs;
